
import { useEffect, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

import { runFetcher } from './api';
import { nodesToDict, edgesToDict, mergeNodes, mergeEdges, nearPos } from './utilsData';
import { setFetcherTraceRunning, setFetcherTraceSuccess, setFetcherTraceError, deleteFetcherTrace } from './utilsData';

import { selectMicroservices } from '../../../features/microservices.slice';
import { useUndoData } from './hookDataUndo';
import { useTimeFilter } from './hookDataTimeFilter';

const useCaseData = ({setCaseChanges}) => {
  const microservices = useSelector(selectMicroservices);

  // TODO: can optimize by keeping the tree of nodes and edges in the app state, and converting when passing to graph component
  const [graphData, setGraphData] = useState({});
  const graphDataRef = useRef({});

  const [filteredGraphData, setFilteredGraphData] = useState({});

  const { undoData, clearUndoData, addUndoLevel, undoToLevel } = useUndoData();

  const { timeFilter, setTimeFilter, filterDefaultGrouping, setFilterDefaultGrouping } = useTimeFilter(graphData, setFilteredGraphData, setCaseChanges);

  const [searchedString, setSearchedString] = useState('');
  const [searchInInfo, setSearchInInfo] = useState(false);

  function deleteGraphData(nodes, edges) {
    const crtGraphData = graphDataRef.current;
    const nodesDict = nodesToDict(nodes);
    const edgesDict = edgesToDict(edges);
    let isNodeBeingDeleted = (type, value) => Boolean(nodesDict[type]?.[value]);

    let remainingNodes = crtGraphData.nodes.filter(node => !isNodeBeingDeleted(node.type, node.value));
    let remainingEdges = crtGraphData.edges.filter(edge => {
      if (edgesDict[edge.from_type]?.[edge.from_value]?.[edge.to_type]?.[edge.to_value]) return false;
      if (isNodeBeingDeleted(edge.from_type, edge.from_value)) return false;
      if (isNodeBeingDeleted(edge.to_type, edge.to_value)) return false;
      return true;
    });

    addUndoLevel(
      {op: 'delete', nodeCount: nodes.length, edgeCount: edges.length},
      crtGraphData,
      {dn: crtGraphData.nodes.length - remainingNodes.length, de: crtGraphData.edges.length - remainingEdges.length}
    );
    
    setCaseChanges(true);
    graphDataRef.current = {
      nodes: remainingNodes,
      edges: remainingEdges,
    };
    setGraphData(graphDataRef.current);
  }

  function setGraphDataAndRefs(newGraphData) {
    clearUndoData();
    graphDataRef.current = newGraphData;
    setGraphData(newGraphData);
  }

  function setGraphDataToUndoLevel(undoLevel) {
    const oldGraphData = undoToLevel(undoLevel);
    setCaseChanges(true);
    graphDataRef.current = oldGraphData;
    setGraphData(graphDataRef.current);
  }

  function upsertGraphData(nodes, edges, defaultPosition, operationInfo) {
    const crtGraphData = graphDataRef.current;

    const graphDataDict = { nodes: nodesToDict(crtGraphData.nodes), edges: edgesToDict(crtGraphData.edges) };

    const newNodes = [];
    const modifiedNodes = [];
    for (let node of nodes) {
      const { type, value } = node;
      if (graphDataDict.nodes[type]?.[value]) {
        const mergedNode = mergeNodes(graphDataDict.nodes[type][value], node);
        if (mergedNode !== graphDataDict.nodes[type][value])
          modifiedNodes.push(mergedNode);
      } else {
        // need to merge so that `info` gets transformed
        const mergedNode = mergeNodes({ type, value }, node);
        mergedNode.position = nearPos(defaultPosition || {x: 0, y: 0});
        newNodes.push(mergedNode);
      }
    }

    let finalNodes;
    if (newNodes.length === 0 && modifiedNodes.length === 0) {
      finalNodes = crtGraphData.nodes;
    } else if (modifiedNodes.length === 0) {
      finalNodes = [...crtGraphData.nodes, ...newNodes];
    } else {
      const modifiedNodesDict = nodesToDict(modifiedNodes);
      const unmodifiedNodes = crtGraphData.nodes.filter(node => !(modifiedNodesDict[node.type]?.[node.value]));
      finalNodes = [...unmodifiedNodes, ...modifiedNodes, ...newNodes];
    }

    const newEdges = [];
    const modifiedEdges = [];
    for (let edge of edges) {
      const { from_type, from_value, to_type, to_value } = edge;
      if (graphDataDict.edges[from_type]?.[from_value]?.[to_type]?.[to_value]) {
        const mergedEdge = mergeEdges(graphDataDict.edges[from_type][from_value][to_type][to_value], edge);
        if (mergedEdge !== graphDataDict.edges[from_type][from_value][to_type][to_value])
          modifiedEdges.push(mergedEdge);
      } else {
        // need to merge so that `info` gets transformed
        const mergedEdge = mergeEdges({ from_type, from_value, to_type, to_value }, edge);
        newEdges.push(mergedEdge);
      }
    }
    let finalEdges;
    if (newEdges.length === 0 && modifiedEdges.length === 0) {
      finalEdges = crtGraphData.edges;
    } else if (modifiedEdges.length === 0) {
      finalEdges = [...crtGraphData.edges, ...newEdges];
    } else {
      const modifiedEdgesDict = edgesToDict(modifiedEdges);
      const unmodifiedEdges = crtGraphData.edges.filter(edge => !(modifiedEdgesDict[edge.from_type]?.[edge.from_value]?.[edge.to_type]?.[edge.to_value]));
      finalEdges = [...unmodifiedEdges, ...modifiedEdges, ...newEdges];
    }

    if (finalNodes === crtGraphData.nodes && finalEdges === crtGraphData.edges) return;

    if (operationInfo) {
      addUndoLevel(operationInfo, graphDataRef.current, {nn: newNodes.length, mn: modifiedNodes.length, ne: newEdges.length, me: modifiedEdges.length});
    }

    graphDataRef.current = {
      nodes: finalNodes,
      edges: finalEdges
    };
    setCaseChanges(true);
    setGraphData(graphDataRef.current);
  }

  function getFetchersForType(type, onlyAutorun = true) {
    if (!(Object.keys(microservices)?.length > 0)) {
      console.warn("Microservices list is empty.");
      return [];
    }
    const ret = [];
    for (let msId in microservices) {
      if (microservices[msId].status === -1) continue;
      if (onlyAutorun && microservices[msId].status === 0) continue;
      for (let fetcher of microservices[msId].fetchers) {
        if (fetcher.status === -1) continue;
        if (onlyAutorun && fetcher.status === 0) continue;
        if (type in fetcher.types || '*' in fetcher.types) {
          ret.push({ microserviceId: msId, fetcher });
        }
      }
    }
    return ret;
  }

  function searchNodesAuto(nodes, defaultPosition) {
    for (let node of nodes) {
      const fetchersInfo = getFetchersForType(node.type);
      for (let fetcherInfo of fetchersInfo) {
        searchNodeWithFetcher(node, fetcherInfo.microserviceId, fetcherInfo.fetcher.name, defaultPosition);
      }
    }
    const graphNodes = nodes.map(node => ({ type: node.type, value: node.value, manuallyAdded: true, visjsProps: { mass: 1.5 } }));
    upsertGraphData(graphNodes, []);
  }

  function searchNodeWithFetcher(node, microserviceId, fetcher, defaultPosition) {
    if (!(microservices?.[microserviceId]?.fetchers?.length > 0)) {
      console.warn("Microservice not found in redux state: ", microserviceId, microservices);
      return;
    }
    console.log("Searching", node, 'with', fetcher, '...');
    const traceNode = { type: node.type, value: node.value };
    setFetcherTraceRunning(traceNode, microserviceId, fetcher, timeFilter);
    upsertGraphData([traceNode], []);

    runFetcher(node.type, node.value, microservices[microserviceId].uri, fetcher, timeFilter)
      .then(({ nodes, edges }) => {
        //console.log("SUCCESS ", node, microservices[microserviceId], fetcher, ': ', nodes, edges);
        const operationInfo = { op: 'search', ms: microservices[microserviceId].name, fetcher, type: node.type, value: node.value }
        upsertGraphData(nodes, edges, defaultPosition, operationInfo);
        setFetcherTraceSuccess(traceNode, microserviceId, fetcher, timeFilter);
        upsertGraphData([traceNode], []);
      })
      .catch(error => {
        //console.log("ERROR ", node, microservices[microserviceId], fetcher, ': ', error);
        setFetcherTraceError(traceNode, microserviceId, fetcher, timeFilter, error.message);
        upsertGraphData([traceNode], []);
      });
  }

  return {
    microservices,

    graphData,
    setGraphDataAndRefs,
    filteredGraphData,
    searchedString, setSearchedString,
    searchInInfo, setSearchInInfo,
    timeFilter, setTimeFilter,
    filterDefaultGrouping, setFilterDefaultGrouping,

    undoData,
    setGraphDataToUndoLevel,

    upsertGraphData,
    deleteGraphData,
    searchNodesAuto,
    searchNodeWithFetcher
  };
};

export { useCaseData };
